import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Bookmark, BookmarkCheck, Search, MapPin, GraduationCap } from "lucide-react";

const REGIONS = ["Both", "India", "Global"];
const FIELDS = ["All", "Engineering", "Medical", "Law", "Commerce", "Business", "Sports", "Architecture", "Design & Arts", "Teaching"];

export default function Colleges() {
  const [colleges, setColleges] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState("Both");
  const [field, setField] = useState("All");
  const [q, setQ] = useState("");

  const loadAll = async () => {
    const [c, s] = await Promise.all([
      api.get("/colleges/", { params: {
        region: region === "Both" ? undefined : region,
        field: field === "All" ? undefined : field,
        q: q || undefined,
      }}),
      api.get("/colleges/saved/list").catch(() => ({ data: [] })),
    ]);
    setColleges(c.data);
    setSaved(s.data.map((x) => x.college_id));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll().finally(() => setLoading(false)); }, [region, field]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(loadAll, 300); return () => clearTimeout(t); }, [q]);

  const toggleSave = async (c) => {
    try {
      if (saved.includes(c.id)) {
        await api.delete(`/colleges/save/${c.id}`);
        setSaved(saved.filter((x) => x !== c.id));
      } else {
        await api.post("/colleges/save", { college_id: c.id });
        setSaved([...saved, c.id]);
      }
    } catch { toast.error("Action failed"); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="colleges-page">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Explore</p>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Colleges & Courses</h1>
        <p className="text-slate-400 mt-2 max-w-xl">Top colleges in India and abroad — filter by field, region and entrance exam.</p>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 border border-white/10 bg-white/5 rounded-full px-4 py-2 min-w-[260px]">
            <Search className="w-4 h-4 text-slate-400" />
            <input data-testid="colleges-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search colleges…" className="bg-transparent outline-none text-white text-sm w-full placeholder:text-slate-400" />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 uppercase tracking-[0.15em] font-bold">Region</span>
            {REGIONS.map((r) => (
              <button key={r} data-testid={`region-filter-${r}`}
                onClick={() => setRegion(r)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${region === r ? "text-white" : "border-white/10 text-slate-300 hover:bg-white/5"}`}
                style={region === r ? { background: "#3B82F6", borderColor: "#3B82F6" } : {}}>
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {FIELDS.map((f) => (
            <button key={f} data-testid={`field-filter-${f}`}
              onClick={() => setField(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${field === f ? "text-slate-900" : "border-white/10 text-slate-300 hover:bg-white/5"}`}
              style={field === f ? { background: "#FBBF24", borderColor: "#FBBF24" } : {}}>
              {f}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="mt-8 text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {colleges.map((c) => (
              <div key={c.id} data-testid={`college-card-${c.id}`} className="brimi-card p-5 flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: c.region === "India" ? "#DBEAFE" : "#FEE2E2", color: c.region === "India" ? "#1D4ED8" : "#B91C1C" }}>
                    <GraduationCap className="w-5 h-5" />
                  </div>
                  <button data-testid={`save-college-${c.id}`} onClick={() => toggleSave(c)} className="text-slate-400 hover:text-coral-500" style={{ color: saved.includes(c.id) ? "#FB7185" : undefined }}>
                    {saved.includes(c.id) ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
                  </button>
                </div>
                <p className="font-display text-lg text-slate-900 mt-3">{c.name}</p>
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.location}</p>
                <p className="text-sm text-slate-700 mt-3 line-clamp-2">{c.notes}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: c.region === "India" ? "#DBEAFE" : "#FEE2E2", color: c.region === "India" ? "#1D4ED8" : "#B91C1C" }}>{c.region}</span>
                  {c.fields.slice(0, 2).map((f) => <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">{f}</span>)}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5 pt-3 border-t border-slate-100">
                  {c.entrance.slice(0, 2).map((e) => <span key={e} className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-800 font-bold border border-yellow-100">📝 {e}</span>)}
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold ml-auto">{c.fees}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
